import { FC, useCallback, useEffect, useState } from 'react';
import {
  Tag,
  Button,
  InlineNotification,
  DefinitionTooltip,
} from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { PageHeader } from 'components/page-header';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { formatNumber } from 'utils/number';
import request from 'lib/request';
import { blobDrainStatusAtom } from './atoms';
import type {
  DrainEstimateCoefficients,
  DrainQueueBucketStats,
  DrainQueueConfig,
  DrainQueueItem,
  BlobDrainConfig,
  BlobDrainStats,
  QueueStats,
  VersionBreakdown,
  RecentDrain,
  BusyJob,
  EvalOutput,
  PaginatedResponse,
} from './types';
import styles from './page.module.scss';

const Tip: FC<{ text: string; children: React.ReactNode }> = ({
  text,
  children,
}) => (
  <DefinitionTooltip definition={text} align="bottom">
    {children}
  </DefinitionTooltip>
);

const fmt = (n: number) => formatNumber(n, 0, 0, 'decimal', true);

const fmtPhase = (val: number | null | undefined) => {
  if (val === null || val === undefined) return '-';
  return val < 60
    ? `${formatNumber(val, 1, 1, 'decimal')}s`
    : `${formatNumber(val / 60, 1, 1, 'decimal')}m`;
};

const FormulaBlock: FC<{ estimates: DrainEstimateCoefficients[] }> = ({
  estimates,
}) => (
  <div className={styles.formulaBlock}>
    {estimates.map((est) => (
      <div key={est.version} className={styles.versionFormula}>
        <strong className={styles.versionLabel}>v{est.version}</strong>
        <code className={styles.formulaCode}>
          est_seconds = {est.baseSeconds} + (rows × {est.rowCoefficient}) +
          (slots × {est.slotCoefficient}) + (has_raw_response ?{' '}
          {est.rawResponseSeconds} : 0)
        </code>
        <div className={styles.formulaEnvVars}>
          <span>
            DRAIN_ESTIMATE_V{est.version}_BASE_SECONDS={est.baseSeconds}
          </span>
          <span>
            DRAIN_ESTIMATE_V{est.version}_ROW_COEFFICIENT={est.rowCoefficient}
          </span>
          <span>
            DRAIN_ESTIMATE_V{est.version}_SLOT_COEFFICIENT={est.slotCoefficient}
          </span>
          <span>
            DRAIN_ESTIMATE_V{est.version}_RAW_RESPONSE_SECONDS=
            {est.rawResponseSeconds}
          </span>
        </div>
      </div>
    ))}
  </div>
);

// ── Sort column mapping (frontend accessorKey → backend column) ─────────

const RECENT_DRAINS_SORT_MAP: Record<string, string> = {
  evaluationOutputId: 'evaluation_output_id',
  slotMonthsDrained: 'slot_months_drained',
  rawResponseDrained: 'raw_response_drained',
  slotMonthsRowCount: 'slot_months_row_count',
  rawResponseDrainedAt: 'raw_response_drained_at',
  slotMonthsDrainedAt: 'slot_months_drained_at',
};

// ── Server-side paginated data hook ─────────────────────────────────────

function usePaginatedData<T>(
  url: string,
  sortMap?: Record<string, string>,
  extraParams?: Record<string, string>
) {
  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (sortColumn) {
        const backendCol = sortMap?.[sortColumn] ?? sortColumn;
        params.set('sortColumn', backendCol);
        params.set('sortDirection', sortDirection);
      }
      if (search) params.set('search', search);
      if (extraParams) {
        Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
      }
      columnFilters.forEach((f) => {
        const backendCol = sortMap?.[f.id] ?? f.id;
        params.set(`filter[${backendCol}]`, String(f.value));
      });

      const response = await request.get<PaginatedResponse<T>>(
        `${url}?${params.toString()}`
      );
      if ('error' in response) throw new Error(response.error);
      setData(response.rows);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Fetch failed'));
    } finally {
      setIsLoading(false);
    }
  }, [
    url,
    page,
    pageSize,
    sortColumn,
    sortDirection,
    search,
    columnFilters,
    sortMap,
    extraParams,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLoadData = useCallback(
    ({ page: p, pageSize: ps }: TableState) => {
      setPage(p);
      setPageSize(ps);
    },
    []
  );

  const handleSortChange = useCallback((sorting: SortingState) => {
    if (sorting.length === 0) {
      setSortColumn(null);
    } else {
      setSortColumn(sorting[0].id);
      setSortDirection(sorting[0].desc ? 'desc' : 'asc');
    }
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    setColumnFilters(filters);
    setPage(1);
  }, []);

  return {
    data,
    totalCount,
    isLoading,
    error,
    handleLoadData,
    handleSortChange,
    handleSearchChange,
    handleFilterChange,
    refresh: fetchData,
  };
}

// ── Static sections (config, stats, queue) ──────────────────────────────

const ConfigSection: FC<{ config: BlobDrainConfig }> = ({ config }) => (
  <div className={styles.section}>
    <h4>Configuration</h4>
    <div className={styles.configGrid}>
      <Tip text="EVAL_OUTPUT_BLOB_DRAIN_ENABLED — master switch for the drain poller">
        <Tag type={config.enabled ? 'green' : 'red'} size="sm">
          {config.enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      </Tip>
      <Tip text="EVAL_OUTPUT_DRAIN_DELAY_SECONDS — minimum age before an eval is eligible for draining">
        <Tag type="gray" size="sm">
          Delay: {Math.round(config.delaySeconds / 3600)}h
        </Tag>
      </Tip>
      <Tip text="EVAL_OUTPUT_DRAIN_POLL_INTERVAL_HOURS — how often the poller reschedules itself">
        <Tag type="gray" size="sm">
          Poll: {config.pollIntervalHours}h
        </Tag>
      </Tip>
      <Tip text="EVAL_OUTPUT_DRAIN_BATCH_SIZE — max eval outputs enqueued per poll cycle">
        <Tag type="gray" size="sm">
          Batch: {config.batchSize}
        </Tag>
      </Tip>
      <Tip text="AZURE_EVAL_OUTPUT_CONTAINER — Azure Blob Storage container for drained files">
        <Tag type="gray" size="sm">
          Container: {config.container}
        </Tag>
      </Tip>
      <Tip text="EVAL_OUTPUT_BLOB_DELETE_AFTER_DRAIN — if true, deletes DB rows after successful drain">
        <Tag type={config.deleteAfterDrain ? 'red' : 'teal'} size="sm">
          Delete after drain: {config.deleteAfterDrain ? 'Yes' : 'No'}
        </Tag>
      </Tip>
    </div>
  </div>
);

const StatsSection: FC<{ stats: BlobDrainStats }> = ({ stats }) => (
  <div className={styles.section}>
    <h4>Drain Progress</h4>
    <div className={styles.configGrid}>
      <Tip text="Total evaluation_outputs rows in the database">
        <Tag type="blue" size="sm">
          Total Outputs: {fmt(stats.totalOutputs)}
        </Tag>
      </Tip>
      <Tip text="Eval outputs with both slot_months and raw_response fully drained to Azure">
        <Tag type="green" size="sm">
          Fully Drained: {fmt(stats.fullyDrained)}
        </Tag>
      </Tip>
      <Tip text="Eval outputs where slot_months is drained but raw_response is still pending">
        <Tag type="teal" size="sm">
          Slot Months Only: {fmt(stats.slotMonthsOnly)}
        </Tag>
      </Tip>
      <Tip text="Eval outputs with no OutputBlob record yet — never been touched by the drain">
        <Tag type="gray" size="sm">
          No Blob: {fmt(stats.noBlob)}
        </Tag>
      </Tip>
      <Tip text="Percentage of all eval outputs that are fully drained">
        <Tag type={stats.percentComplete > 50 ? 'green' : 'blue'} size="sm">
          Complete: {stats.percentComplete}%
        </Tag>
      </Tip>
    </div>
  </div>
);

const DrainQueueSection: FC<{
  stats: DrainQueueBucketStats[];
  config: DrainQueueConfig;
}> = ({ stats, config }) => {
  const concurrencyFor = (bucket: string) => {
    if (bucket === 'small') return config.smallConcurrency;
    if (bucket === 'medium') return config.mediumConcurrency;
    return config.largeConcurrency;
  };

  const slotThreshold = (bucket: string) => {
    if (bucket === 'small') return `≤ ${fmt(config.smallMaxSlots)} slots`;
    if (bucket === 'large') return `≥ ${fmt(config.largeMinSlots)} slots`;
    return `${fmt(config.smallMaxSlots + 1)}–${fmt(
      config.largeMinSlots - 1
    )} slots`;
  };

  return (
    <div className={styles.section}>
      <h4>Drain Queue</h4>
      <div className={styles.configGrid}>
        {stats.map((b) => (
          <Tip
            key={b.bucket}
            text={`${b.bucket}: ${slotThreshold(
              b.bucket
            )}, max ${concurrencyFor(b.bucket)} concurrent`}
          >
            <Tag
              type={
                b.bucket === 'small'
                  ? 'green'
                  : b.bucket === 'medium'
                    ? 'teal'
                    : 'red'
              }
              size="sm"
            >
              {b.bucket}: {fmt(b.pending)} pending / {fmt(b.processing)}{' '}
              processing / {fmt(b.failed)} failed
            </Tag>
          </Tip>
        ))}
        <Tip text="DRAIN_QUEUE_FILL_TARGET — target number of items in the queue">
          <Tag type="blue" size="sm">
            Fill Target: {fmt(config.fillTarget)}
          </Tag>
        </Tip>
      </div>
    </div>
  );
};

const QueueSection: FC<{ queueStats: QueueStats }> = ({ queueStats }) => (
  <div className={styles.section}>
    <h4>Queue Status</h4>
    <div className={styles.configGrid}>
      <Tip text="Jobs waiting in the eval2_drain Sidekiq queue">
        <Tag type="blue" size="sm">
          Drain Queued: {fmt(queueStats.eval2DrainQueued)}
        </Tag>
      </Tip>
      <Tip text="Drain jobs currently being executed by worker threads">
        <Tag type="cyan" size="sm">
          Drain Busy: {fmt(queueStats.drainBusy)}
        </Tag>
      </Tip>
      <Tip text="Drain jobs in Sidekiq retry set (failed and waiting to retry)">
        <Tag type="warm-gray" size="sm">
          Drain Retry: {fmt(queueStats.drainRetry)}
        </Tag>
      </Tip>
      <Tip text="Poller jobs scheduled for future execution">
        <Tag type="purple" size="sm">
          Poller Scheduled: {fmt(queueStats.pollerScheduled)}
        </Tag>
      </Tip>
    </div>
  </div>
);

// ── Column Definitions ──────────────────────────────────────────────────

type BusyRow = BusyJob & Record<string, unknown>;

const busyColumns: DataTableColumnDef<BusyRow>[] = [
  {
    accessorKey: 'evaluationOutputId',
    header: 'Output ID',
    size: 120,
    cell: ({ getValue }) => (
      <a
        className={styles.link}
        href={`/admin/development_area_evaluation_outputs/${getValue()}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {getValue() as number}
      </a>
    ),
  },
  {
    accessorKey: 'drainQueueBucket',
    header: 'Bucket',
    size: 90,
    cell: ({ getValue }) => (getValue() as string) ?? 'standalone',
  },
  {
    accessorKey: 'elapsedSeconds',
    header: 'Running For',
    size: 120,
    cell: ({ getValue }) => `${getValue()}s`,
  },
  {
    accessorKey: 'runAt',
    header: 'Started At',
    size: 200,
    cell: ({ getValue }) => new Date(getValue() as string).toLocaleString(),
  },
  {
    accessorKey: 'process',
    header: 'Process',
    size: 160,
  },
];

type VersionRow = VersionBreakdown & Record<string, unknown>;

const versionColumns: DataTableColumnDef<VersionRow>[] = [
  {
    accessorKey: 'version',
    header: 'Version',
    size: 100,
    cell: ({ getValue }) => `v${getValue()}`,
  },
  {
    accessorKey: 'total',
    header: 'Total',
    size: 120,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'withBlob',
    header: 'With Blob',
    size: 120,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'fullyDrained',
    header: 'Fully Drained',
    size: 120,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'remaining',
    header: 'Remaining',
    size: 120,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
];

type RecentRow = RecentDrain & Record<string, unknown>;

const recentColumns: DataTableColumnDef<RecentRow>[] = [
  {
    accessorKey: 'evaluationOutputId',
    header: () => <Tip text="Evaluation output primary key">Output ID</Tip>,
    size: 120,
    cell: ({ getValue }) => (
      <a
        className={styles.link}
        href={`/admin/development_area_evaluation_outputs/${getValue()}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {getValue() as number}
      </a>
    ),
  },
  {
    accessorKey: 'version',
    header: () => <Tip text="Eval API version (v1/v2/v3)">Version</Tip>,
    size: 80,
    cell: ({ getValue }) => `v${getValue()}`,
  },
  {
    accessorKey: 'slotMonthsDrained',
    header: () => (
      <Tip text="Whether slot_months data has been migrated to Azure Parquet">
        Slot Months
      </Tip>
    ),
    size: 120,
    cell: ({ getValue }) => (
      <Tag type={getValue() ? 'green' : 'gray'} size="sm">
        {getValue() ? 'Yes' : 'No'}
      </Tag>
    ),
  },
  {
    accessorKey: 'rawResponseDrained',
    header: () => (
      <Tip text="Whether raw_response JSON has been migrated to Azure and nil'd">
        Raw Response
      </Tip>
    ),
    size: 120,
    cell: ({ getValue }) => (
      <Tag type={getValue() ? 'green' : 'gray'} size="sm">
        {getValue() ? 'Yes' : 'No'}
      </Tag>
    ),
  },
  {
    accessorKey: 'slotsCount',
    header: () => (
      <Tip text="Number of distinct slots in this evaluation output">Slots</Tip>
    ),
    size: 80,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'slotMonthsRowCount',
    header: () => (
      <Tip text="Number of rows in the slot_months Parquet file (one per slot per month)">
        Rows
      </Tip>
    ),
    size: 100,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'estimatedDrainTime',
    header: () => (
      <Tip text="Estimated drain duration based on slots, rows, and raw_response size">
        Est. Time
      </Tip>
    ),
    size: 100,
    enableSorting: false,
  },
  {
    accessorKey: 'actualDrainSeconds',
    header: () => (
      <Tip text="Actual drain duration. Tilde (~) prefix means timing may include limiter retry wait (pre-instrumentation).">
        Actual
      </Tip>
    ),
    size: 100,
    enableSorting: false,
    cell: ({ getValue, row }) => {
      const val = getValue() as number | null;
      if (val === null || val === undefined) return '-';
      const accurate = (row.original as RecentDrain).timingAccurate;
      const formatted =
        val < 60 ? `${val}s` : `${formatNumber(val / 60, 1, 1, 'decimal')}m`;
      if (accurate) return formatted;
      return <span className={styles.inaccurateTiming}>~{formatted}</span>;
    },
  },
  {
    id: 'phaseSerialize',
    header: () => (
      <Tip text="Time to query DB and serialize to Parquet">Serialize</Tip>
    ),
    size: 90,
    enableSorting: false,
    cell: ({ row }) =>
      fmtPhase((row.original as RecentDrain).phaseSeconds?.serialize),
  },
  {
    id: 'phaseUpload',
    header: () => <Tip text="Time to upload Parquet to Azure">Upload</Tip>,
    size: 90,
    enableSorting: false,
    cell: ({ row }) =>
      fmtPhase((row.original as RecentDrain).phaseSeconds?.upload),
  },
  {
    id: 'phaseVerify',
    header: () => (
      <Tip text="Time to download, hash check, and verify row count + aggregates">
        Verify
      </Tip>
    ),
    size: 90,
    enableSorting: false,
    cell: ({ row }) =>
      fmtPhase((row.original as RecentDrain).phaseSeconds?.verify),
  },
  {
    id: 'phasePostSM',
    header: () => (
      <Tip text="Time for cashflows_by_slot + raw_response after slot_months">
        Post-SM
      </Tip>
    ),
    size: 90,
    enableSorting: false,
    cell: ({ row }) =>
      fmtPhase((row.original as RecentDrain).phaseSeconds?.postSlotMonths),
  },
  {
    accessorKey: 'rawResponseDrainedAt',
    header: 'Completed At',
    size: 180,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleString() : '-';
    },
  },
];

const allOutputColumns: DataTableColumnDef<EvalOutput>[] = [
  {
    accessorKey: 'id',
    header: () => <Tip text="Evaluation output primary key">Output ID</Tip>,
    size: 120,
    cell: ({ getValue }) => (
      <a
        className={styles.link}
        href={`/admin/development_area_evaluation_outputs/${getValue()}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {getValue() as number}
      </a>
    ),
  },
  {
    accessorKey: 'version',
    header: () => <Tip text="Eval API version (v1/v2/v3)">Version</Tip>,
    size: 80,
    cell: ({ getValue }) => `v${getValue()}`,
  },
  {
    accessorKey: 'slotsCount',
    header: () => (
      <Tip text="Number of distinct slots in this evaluation output">Slots</Tip>
    ),
    size: 80,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'createdAt',
    header: () => (
      <Tip text="When this evaluation output was created">Created</Tip>
    ),
    size: 200,
    cell: ({ getValue }) => new Date(getValue() as string).toLocaleString(),
  },
  {
    accessorKey: 'hasRawResponse',
    header: () => (
      <Tip text="Whether raw_response JSON is still in the database (nil after drain)">
        Raw Response
      </Tip>
    ),
    size: 120,
    cell: ({ getValue }) => (
      <Tag type={getValue() ? 'green' : 'gray'} size="sm">
        {getValue() ? 'Present' : 'Nil'}
      </Tag>
    ),
  },
  {
    accessorKey: 'slotMonthsDrained',
    header: () => (
      <Tip text="Whether slot_months data has been migrated to Azure Parquet">
        Slot Months
      </Tip>
    ),
    size: 120,
    cell: ({ getValue }) => (
      <Tag type={getValue() ? 'green' : 'gray'} size="sm">
        {getValue() ? 'Drained' : 'Pending'}
      </Tag>
    ),
  },
  {
    accessorKey: 'rawResponseDrained',
    header: () => (
      <Tip text="Whether raw_response JSON has been migrated to Azure and nil'd">
        Raw Resp Drained
      </Tip>
    ),
    size: 140,
    cell: ({ getValue }) => (
      <Tag type={getValue() ? 'green' : 'gray'} size="sm">
        {getValue() ? 'Drained' : 'Pending'}
      </Tag>
    ),
  },
  {
    accessorKey: 'slotMonthsRowCount',
    header: () => (
      <Tip text="Number of rows in the slot_months Parquet file (one per slot per month)">
        Rows
      </Tip>
    ),
    size: 100,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'estimatedDrainTime',
    header: () => (
      <Tip text="Estimated drain duration based on slots, rows, and raw_response size">
        Est. Time
      </Tip>
    ),
    size: 100,
    enableSorting: false,
  },
  {
    accessorKey: 'actualDrainSeconds',
    header: () => (
      <Tip text="Actual drain duration. Tilde (~) prefix means timing may include limiter retry wait (pre-instrumentation).">
        Actual
      </Tip>
    ),
    size: 100,
    enableSorting: false,
    cell: ({ getValue, row }) => {
      const val = getValue() as number | null;
      if (val === null || val === undefined) return '-';
      const accurate = (row.original as EvalOutput).timingAccurate;
      const formatted =
        val < 60 ? `${val}s` : `${formatNumber(val / 60, 1, 1, 'decimal')}m`;
      if (accurate) return formatted;
      return <span className={styles.inaccurateTiming}>~{formatted}</span>;
    },
  },
  {
    accessorKey: 'drainStatus',
    header: () => (
      <Tip text="Drained = both slot_months and raw_response migrated to Azure">
        Drain Status
      </Tip>
    ),
    size: 120,
    cell: ({ getValue }) => {
      const status = getValue() as string;
      const type =
        status === 'Drained' ? 'green' : status === 'Partial' ? 'teal' : 'gray';
      return (
        <Tag type={type} size="sm">
          {status}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'packageName',
    header: () => (
      <Tip text="Package evaluation that references this output">
        Package Eval
      </Tip>
    ),
    size: 200,
    cell: ({ row }) => {
      const { packageEvaluationId, packageId, packageName } = row.original;
      if (!packageEvaluationId || !packageId) return '-';
      return (
        <a
          className={styles.link}
          href={`/packages/${packageId}/evaluations/${packageEvaluationId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {packageName ?? `Package #${packageId}`}
        </a>
      );
    },
  },
];

// ── Drain Queue Columns ─────────────────────────────────────────────────

type DrainQueueRow = DrainQueueItem;

const drainQueueColumns: DataTableColumnDef<DrainQueueRow>[] = [
  {
    accessorKey: 'evaluationOutputId',
    header: 'Output ID',
    size: 120,
    cell: ({ getValue }) => (
      <a
        className={styles.link}
        href={`/admin/development_area_evaluation_outputs/${getValue()}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {getValue() as number}
      </a>
    ),
  },
  {
    accessorKey: 'sizeBucket',
    header: 'Bucket',
    size: 100,
    meta: {
      filterOptions: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large' },
      ],
    },
    cell: ({ getValue }) => {
      const bucket = getValue() as string;
      const type =
        bucket === 'small' ? 'green' : bucket === 'medium' ? 'teal' : 'red';
      return (
        <Tag type={type} size="sm">
          {bucket}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'estimatedRows',
    header: 'Est. Rows',
    size: 100,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'estimatedSeconds',
    header: 'Est. Time',
    size: 100,
    cell: ({ getValue }) => {
      const val = getValue() as number | null;
      if (val === null || val === undefined) return '-';
      return val < 60
        ? `${formatNumber(val, 1, 1, 'decimal')}s`
        : `${formatNumber(val / 60, 1, 1, 'decimal')}m`;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 110,
    meta: {
      filterOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'failed', label: 'Failed' },
      ],
    },
    cell: ({ getValue }) => {
      const status = getValue() as string;
      const type =
        status === 'pending'
          ? 'gray'
          : status === 'processing'
            ? 'blue'
            : 'red';
      return (
        <Tag type={type} size="sm">
          {status}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'errorMessage',
    header: 'Error',
    size: 250,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ?? '-';
    },
  },
  {
    accessorKey: 'claimedAt',
    header: 'Claimed At',
    size: 180,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleString() : '-';
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Queued At',
    size: 180,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleString() : '-';
    },
  },
];

const DRAIN_QUEUE_SORT_MAP: Record<string, string> = {
  sizeBucket: 'sizeBucket',
  status: 'status',
};

const ALL_OUTPUTS_SORT_MAP: Record<string, string> = {
  id: 'id',
  version: 'version',
  createdAt: 'created_at',
};

const STATIC_TABLE_CONFIG = {
  columnFilters: false,
  globalSearch: false,
  hideFooter: true,
} as const;

// ── Page Component ──────────────────────────────────────────────────────

export const BlobDrainStatusPage: FC = () => {
  const [loadable, refresh] = useAtom(blobDrainStatusAtom);

  const recentDrains = usePaginatedData<RecentRow>(
    '/admin/blob_drain_status/recent_drains',
    RECENT_DRAINS_SORT_MAP
  );

  const allOutputs = usePaginatedData<EvalOutput>(
    '/admin/blob_drain_status/all_outputs',
    ALL_OUTPUTS_SORT_MAP
  );

  const drainQueueItems = usePaginatedData<DrainQueueRow>(
    '/admin/blob_drain_status/drain_queue_items',
    DRAIN_QUEUE_SORT_MAP
  );

  const handleRefresh = useCallback(() => {
    refresh();
    recentDrains.refresh();
    allOutputs.refresh();
    drainQueueItems.refresh();
  }, [refresh, recentDrains, allOutputs, drainQueueItems]);

  if (loadable.state === 'loading') {
    return (
      <>
        <PageHeader breadcrumbs={[]} title="Blob Drain Status" actions={null} />
        <div className={styles.container}>Loading...</div>
      </>
    );
  }

  if (loadable.state === 'hasError') {
    return (
      <>
        <PageHeader breadcrumbs={[]} title="Blob Drain Status" actions={null} />
        <InlineNotification
          kind="error"
          title="Failed to load drain status"
          subtitle={String(loadable.error)}
        />
      </>
    );
  }

  const {
    config,
    stats,
    versionBreakdown,
    queueStats,
    drainEstimates,
    busyJobs,
    drainQueueStats,
    drainQueueConfig,
  } = loadable.data;

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Blob Drain Status"
        actions={
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Renew}
            iconDescription="Refresh"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        }
      />
      <div className={styles.container}>
        <ConfigSection config={config} />
        <StatsSection stats={stats} />
        <DrainQueueSection stats={drainQueueStats} config={drainQueueConfig} />
        <QueueSection queueStats={queueStats} />

        <div className={styles.section}>
          <h4>Busy Jobs ({busyJobs.length})</h4>
          {busyJobs.length === 0 ? (
            <p className={styles.emptyState}>No drain jobs currently running</p>
          ) : (
            <DataTable<BusyRow>
              data={busyJobs as BusyRow[]}
              columns={busyColumns}
              getRowId={(row) => `${row.evaluationOutputId}-${row.process}`}
              config={STATIC_TABLE_CONFIG}
            />
          )}
        </div>

        <div className={styles.section}>
          <h4>By Version</h4>
          <DataTable<VersionRow>
            data={versionBreakdown as VersionRow[]}
            columns={versionColumns}
            getRowId={(row) => String(row.version)}
            config={STATIC_TABLE_CONFIG}
          />
        </div>

        <div className={styles.section}>
          <h4>Drain Queue ({drainQueueItems.totalCount})</h4>
          <DataTable<DrainQueueRow>
            data={drainQueueItems.data}
            columns={drainQueueColumns}
            getRowId={(row) => String(row.id)}
            isLoading={drainQueueItems.isLoading}
            error={drainQueueItems.error}
            config={{}}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: drainQueueItems.totalCount,
            }}
            onPaginationChange={drainQueueItems.handleLoadData}
            onSortChange={(s) => drainQueueItems.handleSortChange(s.sorting)}
            onFilterChange={(s) =>
              drainQueueItems.handleFilterChange(s.columnFilters)
            }
            onGlobalSearchChange={(s) =>
              drainQueueItems.handleSearchChange(s.globalFilter)
            }
            searchPlaceholder="Search by Output ID"
          />
        </div>

        <div className={styles.section}>
          <h4>Recent Drains</h4>
          <FormulaBlock estimates={drainEstimates} />
          <DataTable<RecentRow>
            data={recentDrains.data}
            columns={recentColumns}
            getRowId={(row) => String(row.evaluationOutputId)}
            isLoading={recentDrains.isLoading}
            error={recentDrains.error}
            config={{}}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: recentDrains.totalCount,
            }}
            onPaginationChange={recentDrains.handleLoadData}
            onSortChange={(s) => recentDrains.handleSortChange(s.sorting)}
            onFilterChange={(s) =>
              recentDrains.handleFilterChange(s.columnFilters)
            }
            onGlobalSearchChange={(s) =>
              recentDrains.handleSearchChange(s.globalFilter)
            }
            searchPlaceholder="Search by Output ID"
          />
        </div>

        <div className={styles.section}>
          <h4>All Eval Outputs</h4>
          <FormulaBlock estimates={drainEstimates} />
          <DataTable<EvalOutput>
            data={allOutputs.data}
            columns={allOutputColumns}
            getRowId={(row) => String(row.id)}
            isLoading={allOutputs.isLoading}
            error={allOutputs.error}
            config={{}}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: allOutputs.totalCount,
            }}
            onPaginationChange={allOutputs.handleLoadData}
            onSortChange={(s) => allOutputs.handleSortChange(s.sorting)}
            onFilterChange={(s) =>
              allOutputs.handleFilterChange(s.columnFilters)
            }
            onGlobalSearchChange={(s) =>
              allOutputs.handleSearchChange(s.globalFilter)
            }
            searchPlaceholder="Search by Output ID"
          />
        </div>
      </div>
    </>
  );
};
